import { isEmpty } from 'lodash';
import { Helmet } from 'react-helmet';
import { ETransportType } from '../../../common/types';
import { jsonLdFaq } from '../../../helpers';
import { formatDuration, getRoundPrice } from '../../../helpers/results';
import { NAVIGATION, TITLE_SUFFIX } from '../../App/constants';
import { SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE } from '../constants';

export const buildHelmet = (
  originCity,
  destinationCity,
  shortFaqs,
  tripData,
  product,
  metaImage,
  currentPageType,
  isMainPageType,
  dynamicInformation,
) => {
  let price = '';
  let priceRange = '';
  let durationRange = '';
  let dailyTripsPerDay = '';

  if (dynamicInformation) {
    if (dynamicInformation.price) {
      price = `dès ${getRoundPrice(dynamicInformation.price.min)} euros`;

      priceRange = ` Prix entre : ${getRoundPrice(
        dynamicInformation.price.min,
      )}€ et ${getRoundPrice(dynamicInformation.price.max)}€ ·`;
    }

    if (dynamicInformation.duration) {
      durationRange = ` Durée : entre ${formatDuration(
        dynamicInformation.duration.min,
      )} et ${formatDuration(dynamicInformation.duration.max)} ·`;
    }

    if (dynamicInformation.dailyTripsPerDay !== null) {
      dailyTripsPerDay = ` Trajets quotidien : ${dynamicInformation.dailyTripsPerDay}`;
    }
  }

  const pageTransportType = {
    [ETransportType.BUS]: 'bus',
    [ETransportType.TRAIN]: 'train',
    [ETransportType.CARPOOLING]: 'covoiturage',
  }[SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE[currentPageType]];
  const titleTransportType = pageTransportType ?? 'train, bus et covoiturage';
  const title = `Billet de ${titleTransportType} ${originCity.name} ${destinationCity.name} pas cher ${price}`;
  const description = `${originCity.name} ${destinationCity.name}${priceRange}${durationRange}${dailyTripsPerDay} ${titleTransportType}. Réservez le trajets le moins cher.`;
  const productName = `Billet de ${pageTransportType} ${originCity.name} ${destinationCity.name}`;
  const canonicalUrl = `${window.location.protocol}//${window.location.host}${
    NAVIGATION.SEARCH
  }/${originCity.slug}/${destinationCity.slug}${
    isMainPageType ? '' : `/${currentPageType}`
  }`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta content={description} name="description" />
      <meta content="website" property="og:type" />
      <meta content={window.location.href} property="og:url" />
      <meta content={title + TITLE_SUFFIX} property="og:title" />
      <meta content={description} property="og:description" />
      <meta content={metaImage} property="og:image" />
      <link href={canonicalUrl} rel="canonical" />
      {shortFaqs.length > 0 && (
        <script type="application/ld+json">{jsonLdFaq(shortFaqs)}</script>
      )}

      {tripData && (
        <script type="application/ld+json">
          {JSON.stringify(
            buildTrainTripStructuredData(
              tripData,
              product,
              description,
              productName,
              metaImage,
              dynamicInformation,
            ),
          )}
        </script>
      )}
    </Helmet>
  );
};

const buildTrainTripStructuredData = (
  tripData,
  product,
  description,
  name,
  image,
  dynamicInformation,
) => {
  const lowPrice = dynamicInformation?.minPrice / 100;
  const structuredProduct = !isEmpty(product) && {
    '@type': 'Product',
    name,
    aggregateRating: {
      '@type': 'AggregateRating',
      reviewCount: product.reviewCount,
      ratingValue: product.ratings.mean,
      worstRating: product.ratings.min,
      bestRating: product.ratings.max,
    },
  };

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'TrainTrip',
    description,
    name,
    departureStation: {
      '@type': 'TrainStation',
      name: tripData.cities.origin.localname.split(',')[0],
    },
    arrivalStation: {
      '@type': 'TrainStation',
      name: tripData.cities.destination.localname.split(',')[0],
    },
    image,
    provider: [
      dynamicInformation?.companies.map((company) => ({
        '@type': 'Organization',
        name: company.fullname,
      })),
    ],
    potentialAction: {
      '@context': 'https://schema.org',
      '@type': 'TravelAction',
      distance: `${Math.round(tripData.distanceAsCrowFlies)} km`,
      fromLocation: tripData.cities.origin.uniquename,
      toLocation: tripData.cities.destination.uniquename,
      result: {
        '@type': 'AggregateOffer',

        priceCurrency: 'EUR',
      },
    },
  };

  if (lowPrice) {
    structuredData.potentialAction.result.lowPrice = lowPrice;
  }

  if (structuredProduct) {
    structuredData.potentialAction.result.itemReviewed = structuredProduct;
  }

  return structuredData;
};
